import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { logDebug } from "./logger";

const fmt = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

const fmtData = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) {
    return String(d).split("T")[0].split("-").reverse().join("/");
  }
  return dt.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

const AZUL_ESCURO = [15, 23, 42];

// Dados das lojas da conta original (Óticas Laurindo) para o cabeçalho
const LOJAS = [
  { key: "iririu", nome: "Óticas Laurindo Iririú", endereco: "Rua Guaíra 595, Iririú, Joinville - SC", telefone: "(47) 98894-5195" },
  { key: "colombo", nome: "Óticas Laurindo Colombo", endereco: "Rua Huxley 317, Atuba, Colombo - PR", telefone: "(41) 98833-4547" },
  { key: "itaum", nome: "Óticas Laurindo Itaum", endereco: "Rua Monsenhor Gercino 1286, Itaum, Joinville - SC", telefone: "(47) 93383-7281" },
  { key: "anita", nome: "Óticas Laurindo Anita Garibaldi", endereco: "Rua Padre Kolb 1428, Anita Garibaldi, Joinville - SC", telefone: "(47) 98811-6325" },
];

const contaPrincipal = () => {
  const t = localStorage.getItem("auth_tenant_id");
  return !t || t === "1";
};

let _cfgCache = null;
const carregarConfigLoja = async () => {
  if (_cfgCache !== null) return _cfgCache;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}` },
    });
    _cfgCache = await res.json();
  } catch (err) {
    logDebug("Config indisponível no comprovante:", err);
    _cfgCache = {};
  }
  return _cfgCache;
};

// Cabeçalho do comprovante: dados da própria conta (nunca de outra loja)
const getLoja = async (nomeLoja) => {
  if (contaPrincipal()) {
    const n = (nomeLoja || "").toLowerCase();
    return LOJAS.find((l) => n.includes(l.key)) || { nome: "Óticas Laurindo", endereco: "", telefone: "" };
  }
  const cfg = await carregarConfigLoja();
  const info = ((cfg?.catalogo || {}).lojas || {})[nomeLoja] || {};
  const empresa = cfg?.empresa || nomeLoja || "Ótica";
  return {
    nome: nomeLoja && empresa !== nomeLoja ? `${empresa} — ${nomeLoja}` : empresa,
    endereco: info.endereco || "",
    telefone: info.whatsapp || "",
  };
};

let _logoCache = null;
const carregarLogo = async () => {
  if (_logoCache !== null) return _logoCache;
  try {
    const res = await fetch("/logo-laurindo.jpeg");
    const blob = await res.blob();
    _logoCache = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve("");
      reader.readAsDataURL(blob);
    });
  } catch {
    _logoCache = "";
  }
  return _logoCache;
};

async function montarComprovante(r) {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const loja = await getLoja(r.nome_loja);
  const logo = contaPrincipal() ? await carregarLogo() : "";

  // Cabeçalho: logo + dados da loja
  if (logo) {
    try { doc.addImage(logo, "JPEG", 14, 12, 20, 20); } catch (err) { logDebug("Logo inválido no comprovante:", err); }
  }
  const xText = logo ? 40 : 14;
  doc.setFontSize(15);
  doc.setFont(undefined, "bold");
  doc.text(loja.nome, xText, 18);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  if (loja.endereco) doc.text(loja.endereco, xText, 24);
  if (loja.telefone) doc.text(`Tel: ${loja.telefone}`, xText, 29);

  let y = 36;
  doc.setDrawColor(200);
  doc.line(14, y, pageW - 14, y);
  y += 6;
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text("Comprovante de Compra", pageW / 2, y, { align: "center" });
  doc.setFont(undefined, "normal");
  y += 6;

  doc.setFontSize(10);
  doc.text(`OS: ${r.os || "-"}`, 14, y);
  doc.text(`Data: ${fmtData(r.data_venda || r.data_registro)}`, pageW - 14, y, { align: "right" });
  y += 2;

  autoTable(doc, {
    startY: y,
    head: [["Dados do Cliente", ""]],
    body: [
      ["Nome", r.nome_cliente || "-"],
      ["CPF", r.cpf_cliente || "-"],
      ["Telefone", r.telefone_cliente || "-"],
    ],
    theme: "grid",
    headStyles: { fillColor: AZUL_ESCURO },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 45 } },
    styles: { fontSize: 9 },
  });
  y = doc.lastAutoTable.finalY + 6;

  const produtos = [];
  const nomePrincipal = [r.tipo_produto, r.estilo_armacao].filter(Boolean).join(" - ") || r.tipo_lente || "Produto";
  produtos.push([nomePrincipal, r.nome_laboratorio || "-", fmt(r.valor_lente != null ? r.valor_lente : r.valor_total)]);
  (r.produtos_adicionais || []).forEach((p) => {
    const nome = [p.tipo, p.estilo_armacao].filter(Boolean).join(" - ") || p.tipo_lente || "-";
    produtos.push([nome, p.referencia || p.nome_laboratorio || "-", fmt(p.valor)]);
  });
  autoTable(doc, {
    startY: y,
    head: [["Produto", "Ref./Laboratório", "Valor"]],
    body: produtos,
    theme: "grid",
    headStyles: { fillColor: AZUL_ESCURO },
    columnStyles: { 2: { halign: "right", cellWidth: 32 } },
    styles: { fontSize: 9 },
  });
  y = doc.lastAutoTable.finalY + 6;

  const temGrau = r.od_esf || r.oe_esf || r.od_cil || r.oe_cil;
  if (temGrau) {
    autoTable(doc, {
      startY: y,
      head: [["Olho", "ESF", "CIL", "EIXO", "ADIÇÃO", "DNP", "Altura"]],
      body: [
        ["OD", r.od_esf || "-", r.od_cil || "-", r.od_eixo || "-", r.od_adicao || "-", r.dnp_od || "-", r.altura_od || "-"],
        ["OE", r.oe_esf || "-", r.oe_cil || "-", r.oe_eixo || "-", r.oe_adicao || "-", r.dnp_oe || "-", r.altura_oe || "-"],
      ],
      theme: "grid",
      headStyles: { fillColor: [37, 99, 235] },
      styles: { fontSize: 8, halign: "center" },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  const pag = [];
  (r.formas_pagamento || []).filter((fp) => fp.forma !== "Pendente").forEach((fp) => pag.push([fp.forma, fmt(fp.valor)]));
  (r.quitacoes || []).forEach((q) => pag.push([`${q.forma} (quitação ${fmtData(q.data)})`, fmt(q.valor)]));
  const pendente = (r.formas_pagamento || []).filter((fp) => fp.forma === "Pendente").reduce((s, fp) => s + (fp.valor || 0), 0);
  if (pendente > 0) pag.push(["Saldo Pendente", fmt(pendente)]);
  pag.push([
    { content: "TOTAL", styles: { fontStyle: "bold" } },
    { content: fmt(r.valor_total), styles: { fontStyle: "bold", halign: "right" } },
  ]);
  autoTable(doc, {
    startY: y,
    head: [["Pagamento", "Valor"]],
    body: pag,
    theme: "grid",
    headStyles: { fillColor: AZUL_ESCURO },
    columnStyles: { 1: { halign: "right", cellWidth: 45 } },
    styles: { fontSize: 9 },
  });
  y = doc.lastAutoTable.finalY + 10;

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text("Documento gerado automaticamente — Óticas Laurindo", pageW / 2, y, { align: "center" });

  return doc;
}

const nomeArquivo = (r) => `comprovante_${(r.nome_cliente || "cliente").replace(/\s+/g, "_")}_${r.os || ""}.pdf`;

export async function gerarComprovante(r) {
  const doc = await montarComprovante(r);
  doc.save(nomeArquivo(r));
}

export async function gerarComprovanteBlob(r) {
  const doc = await montarComprovante(r);
  return { blob: doc.output("blob"), nome: nomeArquivo(r) };
}
